import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Video } from "lucide-react";
import { toast } from "sonner";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { cn } from "../../../lib/utils";
import { crmErrorMessage } from "../api/crmErrors";
import { DELIVERY_KEYS } from "../contract";
import { fmtTime } from "../format";
import { clientHref } from "../paths";
import useRole from "../hooks/useRole";
import Chip from "../ui/Chip";
import CrmButton from "../ui/CrmButton";
import PackBalanceBadge from "../ui/PackBalanceBadge";
import ConfirmBadge from "./ConfirmBadge";
import { CRM } from "../../../constants/testIds/crm";

/**
 * One booking of the Today strip (spec Zone 1; contract v1.3: one card per attendee, the
 * cards of one slot side by side): time, client, session type with its online /
 * in-person icon, the pack position ("Session 9 of 10"), the Confirmed badge and — at
 * session time — Mark complete / No-show.
 *
 * "Appear at session time" (spec) is `now >= slot_start` on the live clock (contract
 * v1.3, matching C1.07's ATTENDANCE_TOO_EARLY before the start). `actions_available` is
 * the server's "attendance may be recorded for this booking at all".
 *
 * A tap opens the client: the owner on the Session notes tab at the last note (the
 * worked example "taps it, reads last week's note"); a helper, who may not read notes, on
 * Sessions.
 */
export default function SessionCard({ card, attendee: a, timezone, now, onComplete, onNoShow, onResend, remindersOn = true }) {
  const t = useT("crm");
  const tc = useT("common");
  const { lang } = useLanguage();
  const { can } = useRole();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const started = now.getTime() >= new Date(card.slot_start).getTime();
  const Mode = card.delivery_mode === "video" ? Video : MapPin;

  const open = () => {
    const q = can("viewSessionNotes")
      ? `tab=session-notes${a.last_note_booking_id ? `&booking=${encodeURIComponent(a.last_note_booking_id)}` : ""}`
      : "tab=sessions";
    navigate(clientHref(a.client_id, q));
  };

  const act = async (e, fn) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await fn(a);
    } catch (err) {
      toast.error(crmErrorMessage(err, t, tc));
    } finally {
      setBusy(false);
    }
  };

  const showActions = started && !a.attendance && a.actions_available && a.status !== "cancelled";

  return (
    <article
      data-testid={CRM.todayCard(a.booking_id)}
      data-past={card.is_past ? "true" : "false"}
      onClick={open}
      onKeyDown={(e) => { if (e.key === "Enter" && e.target === e.currentTarget) open(); }}
      tabIndex={0}
      aria-label={t("today.openAria", { name: a.client_name, time: fmtTime(card.slot_start, lang, timezone) })}
      className={cn(
        "w-[208px] shrink-0 snap-start cursor-pointer rounded-2xl bg-eh-cream p-4 hover:bg-eh-cream-deep",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-eh-ink",
        card.is_past && "opacity-60",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <time dateTime={card.slot_start} className="text-lg font-semibold text-eh-ink">{fmtTime(card.slot_start, lang, timezone)}</time>
        <ConfirmBadge bookingId={a.booking_id} confirmed={a.confirmed} disabled={card.is_past} remindersOn={remindersOn} onResend={onResend} />
      </div>
      <p className="mt-2 flex items-center gap-2">
        <span className="truncate text-lg font-semibold text-eh-ink">{a.client_name}</span>
        {a.is_new_client && <Chip variant="filled" data-testid={CRM.todayNew(a.booking_id)}>{t("today.new")}</Chip>}
      </p>
      <p className="mt-1 flex items-center gap-1 text-sm text-eh-ink">
        <span className="truncate">{card.service_name} · {t(DELIVERY_KEYS[card.delivery_mode] || "enum.delivery.in_person")}</span>
        <Mode size={14} aria-hidden="true" className="shrink-0 text-eh-muted" />
      </p>
      {a.pack && <PackBalanceBadge pack={a.pack} variant="session" className="mt-2" />}
      {!a.confirmed && !card.is_past && !a.attendance && remindersOn && <p className="mt-2 text-xs text-eh-muted">{t("today.tapToResend")}</p>}
      {a.attendance && (
        <p data-testid={CRM.todayAttendance(a.booking_id)} className="mt-2 text-xs font-semibold text-eh-ink">
          {a.attendance === "completed" ? t("today.completed") : t("today.noShowDone")}
        </p>
      )}
      {showActions && (
        <div className="mt-3 flex gap-2">
          <CrmButton size="sm" disabled={busy} onClick={(e) => act(e, onComplete)} data-testid={CRM.todayComplete(a.booking_id)}>{t("today.markComplete")}</CrmButton>
          <CrmButton size="sm" variant="outline" disabled={busy} onClick={(e) => act(e, onNoShow)} data-testid={CRM.todayNoShow(a.booking_id)}>{t("today.noShow")}</CrmButton>
        </div>
      )}
    </article>
  );
}
